import { styled } from "@mui/system";
import { Button, ButtonGroup, Stack, Typography } from "@mui/material";

import { darkBlue, organisationLightBlue } from "@/theme/colors";
import { EarlyWarningTableWidgetDataWarningsInnerLevelEnum } from "@/lib/api";

export type WeatherCode = "red" | "orange" | "yellow" | "green";

const getColorForLevel = (level?: WeatherCode) => {
  switch (level) {
    case "yellow":
      // Remove later when new color is tested and approved
      // return "#F9E11E";
      return "#FFED00";
    case "orange":
      // Remove later when new color is tested and approved
      // return "#E17000";
      return "#FF9900";
    case "red":
      // Remove later when new color is tested and approved
      // return "#D52B1E";
      return "#CD0000";
    case "green":
    default:
      return "#39870C";
  }
};
const getBackgroundColorForLevel = (level?: WeatherCode) => {
  switch (level) {
    case "yellow":
      // Remove later when new color is tested and approved
      // return "#FFF8D8";
      return "#FFFDE6";
    case "orange":
      // Remove later when new color is tested and approved
      // return "#FCDECF";
      return "#FFF5E6";
    case "red":
      // Remove later when new color is tested and approved
      // return "#F0D1CC";
      return "#FAE6E6";
    case "green":
    default:
      return "transparent";
  }
};

const Widget = styled("div")`
  height: 100%;

  @media print {
    padding: 20px;
    padding-top: 60px;
  }
`;

const Wrapper = styled(Stack)`
  padding-bottom: 20px;

  @container (max-width: 900px) {
    display: block;
  }
  @media print {
    display: block;
  }
`;

const Logo = styled("div")`
  color: black;
  display: none;
  position: absolute;
  left: 50%;
  transform: translateX(-25%);
  top: 0px;

  @media print {
    display: block;
    width: 160px;
  }
`;

const TableElement = styled("div")`
  background: white;
  border-radius: 8px;
  bottom: var(--spacing-4x);
  box-shadow: 0px 2px 4px color-mix(in srgb, var(--black) 40%, transparent);
  font-size: 12px;
  left: 20px;
  margin-inline: auto;
  max-width: 440px;
  padding: var(--spacing-3x) var(--spacing-4x);
  position: absolute;
  right: 20px;
`;

const Table = styled("table")(`
  -webkit-print-color-adjust: exact !important;
  border: 1px solid white;
  border-spacing: 0;
  width: 100%;
`);

const TableWrapper = styled("div")(`
  max-width: 100%;
  overflow-x: auto;
  width: 100cqw;
`);

const Row = styled("tr")(`
  // work around to allow elements in td to fill its content
  height: 1px;
`);

const HeaderCell = styled("th")(
  ({
    selected,
    variant = "block",
  }: {
    selected?: boolean;
    variant?: "inline" | "block";
  }) => `
  background: ${organisationLightBlue};
  border: 1px solid white;
  font-size: .75em;
  font-weight: normal;
  height: inherit;
  padding: .625em .25em;
  line-height: 1.25em;
  width: 300px;

  ${
    selected
      ? `
    background: ${darkBlue};
    border: 1px solid ${darkBlue};
    border-bottom: 1px solid white;
    color: white;
  `
      : ""
  }

  // type column
  &:first-of-type {
    background: transparent;
    width: 6.375em;
  }

  // first date column
  &:nth-of-type(2) {
    border-top-left-radius: 8px;
  }
  // last column
  &:last-of-type {
    border-top-right-radius: 8px;
  }

  ${variant === "inline" ? "cursor: pointer;" : ""}
`,
);

const BodyCell = styled("td")(
  ({
    selected,
    variant = "block",
  }: {
    selected?: boolean;
    variant?: "inline" | "block";
  }) => `
  background: #F3F3F3;
  border: 1px solid white;
  padding: 0;
  min-width: 64px;

  ${
    variant === "inline"
      ? `
    text-align: center;
    min-width: 0;

    .ValueElement {
      background: none;
      border: none;
      display: inline-flex;
      justify-content: center;
      padding: 0 .365em;
      min-height: auto;
    }

    .ValueElementIndicator {
      height: .6em;
      width: .6em;
    }

    .ValueElementLabel {
      display: none;
    }
  `
      : `
    .ValueElementIndicator {
      cursor: initial;
    }
  `
  }

  ${
    selected
      ? `
    position: relative;
    z-index: 2;

    &::before {
      background: ${darkBlue};
      content: '';
      position: absolute;
      left: -1px;
      width: 2px;
      top: -2px;
      bottom: -1px;
    }
    &::after {
      background: ${darkBlue};
      content: '';
      position: absolute;
      right: -1px;
      width: 2px;
      top: -2px;
      bottom: 0px;
    }
  `
      : ""
  }

  &:first-of-type {
    background: none;
    font-size: .75em;
    text-align: right;

    ${
      selected
        ? `
      border: 1px solid white;
    `
        : ""
    }
  }

  tr:last-of-type > & {
    ${selected ? `border-bottom: 2px solid ${darkBlue};` : ""}
  }
`,
);

const BodyCellTooltip = styled("div")`
  .ValueElement {
    background: none !important;
  }
`;

const TypeElement = styled("div")(`
  align-items: center;
  display: flex;
  font-size: 1;
  gap: .25em;
  height: 2em;
  justify-content: flex-end;
  line-height: 1.2em;
  padding-right: .5em;

  img {
    height: 1.5em;
    margin-left: .25em;
  }
`);

const ValueElement = styled("div")(
  ({
    level,
    multiple,
  }: {
    level?: EarlyWarningTableWidgetDataWarningsInnerLevelEnum;
    multiple?: boolean;
  }) => `
  ${level ? `background: ${getBackgroundColorForLevel(level)};` : ""}

  align-items: center;
  display: flex;
  gap: .5em;
  min-height: 20px;
  padding: 0 0 0 .625em;

  &:not(:first-of-type) {
    border-top: 2px solid white;
    box-sizing: content-box;
  }

  ${!multiple ? "min-height: 40px;" : ""}
`,
);

const ValueElementLabel = styled("span")`
  display: block;
  font-size: 0.75em;
  line-height: 1.25em;
`;

const ValueElementIndicator = styled("span")(
  ({ level }: { level?: EarlyWarningTableWidgetDataWarningsInnerLevelEnum }) => `
  ${`background: ${getColorForLevel(level)};`}
  border-radius: 10px;
  display: inline-block;
  height: 10px;
  width: 10px;

  ${level ? "cursor: pointer;" : ""}
`,
);

const Legend = styled("ul")(`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 0 10px;
  margin-bottom: 0;
  padding-bottom: 8px;
`);

const LegendItem = styled("li")(`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: 8px;
  line-height: 1.2em;
`);

const LegendItemColor = styled("div")(
  ({ level }: { level?: WeatherCode }) => `
  background: ${getColorForLevel(level)};
  border-radius: 6px;
  display: block;
  flex: 0 0 12px;
  height: 12px;
  margin-top: 3px;
  width: 12px;
`,
);

const InfoWrapper = styled("div")`
  align-self: flex-start;
  background: #f3f3f3;
  border-radius: 8px;
  flex-shrink: 0;
  font-size: 14px;
  height: auto;
  padding: 10px;
  max-height: calc(100cqh - 140px);
  overflow-y: auto;
  width: clamp(280px, 33cqw, 500px);

  @container (max-width: 900px) {
    margin-top: 20px;
    width: 100%;
  }

  @media print {
    background: none;
    box-shadow: none;
    display: block;
    max-height: auto;
    position: relative;
    right: 0;
    width: 100%;
  }
`;

const WarningToday = styled(Stack)`
  background: #ebeef1;
  border-radius: 4px;
  color: ${darkBlue};
  font-size: 14px;
  padding: 16px;
  position: absolute;
  width: 300px;
  transform: translate(-50%, -50%);
  text-decoration: none;

  @container (max-width: 900px) {
    order: 3;
    font-size: 12px;
    line-height: 1.2em;
    padding: 8px;
    position: relative;
    transform: none;
    max-width: 300px;
    width: auto;
  }
  @media print {
    display: none;
  }
`;

const WarningButtons = styled(Stack)`
  @container (max-width: 900px) {
    width: 100%;
  }
`;

const PrintButton = styled(Button)`
  @media print {
    display: none;
  }
`;

const Toggle = styled(ButtonGroup)`
  border: 1px solid white;
  outline: 1px solid ${darkBlue};

  @media print {
    display: none;
  }
`;

const MapThumb = styled("div")`
  aspect-ratio: 3/4;
  background-position: center;
  background-size: cover;

  a.mapboxgl-ctrl-logo {
    height: 0;
  }

  @media print {
    canvas {
      display: none;
    }
  }
`;

const SelectedWarningOverlay = styled("div")`
  border-radius: 4px;
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  padding: 8px;
`;

const PrintTitle = styled(Typography)(() => ({
  visibility: "hidden",
  height: 0,
  "@media print": {
    visibility: "visible",
    height: "auto",
  },
}));

export default {
  Widget,
  Wrapper,
  Logo,
  TableElement,
  Table,
  TableWrapper,
  Row,
  HeaderCell,
  BodyCell,
  BodyCellTooltip,
  TypeElement,
  ValueElement,
  ValueElementLabel,
  ValueElementIndicator,
  Legend,
  LegendItem,
  LegendItemColor,
  InfoWrapper,
  WarningToday,
  WarningButtons,
  Toggle,
  PrintButton,
  MapThumb,
  SelectedWarningOverlay,
  PrintTitle,
};
